/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import {
  Children,
  type ForwardedRef,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
} from 'react';
import { type ContextValue, Provider, createLeafComponent, useContextProps } from 'react-aria-components';
import { Avatar, AvatarContext } from '../avatar/avatar.component';
import { Message, MessageContext, type MessageProps } from '../message/message.component';
import { Text, TextContext } from '../text/text.component';

const MESSAGE_GROUP_SLOTS = {
  SENDER_NAME: 'sender-name',
} as const;

interface MessageGroupProps extends Omit<HTMLAttributes<HTMLLIElement>, 'style' | 'className'> {
  isOwn?: boolean;
  style?: stylex.StyleXStyles;
}

const MessageGroupContext = createContext<ContextValue<MessageGroupProps, HTMLLIElement>>({});

const MessageGroup = createLeafComponent('item', (props: MessageGroupProps, ref: ForwardedRef<HTMLLIElement>) => {
  [props, ref] = useContextProps(props, ref, MessageGroupContext);

  const { style, isOwn = false, children, ...coreProps } = props;

  let avatarChild: ReactNode = null;
  let senderNameChild: ReactNode = null;
  const messageChildren: ReactElement<MessageProps>[] = [];
  const otherChildren: ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    if (child.type === Avatar) avatarChild = child;
    else if (child.type === Text) senderNameChild = child;
    else if (child.type === Message) messageChildren.push(child as ReactElement<MessageProps>);
    else otherChildren.push(child);
  });

  return (
    <li
      ref={ref}
      role="group"
      {...stylex.props(styles.base, isOwn ? styles.sent : styles.received, style)}
      {...coreProps}>
      <Provider
        values={[
          [MessageContext, { variant: isOwn ? 'sent' : 'received' }],
          [AvatarContext, { size: 32, style: styles.avatarArea, isDecorative: true }],
          [
            TextContext,
            {
              size: 13,
              style: styles.senderNameArea,
              weight: 'regular',
              color: 'placeholder',
            },
          ],
        ]}>
        {senderNameChild}
        {avatarChild}
        <ol {...stylex.props(styles.messageList)}>{stackMessages(messageChildren)}</ol>
        {otherChildren}
      </Provider>
    </li>
  );
});

function stackMessages(messages: ReactElement<MessageProps>[]): ReactElement<MessageProps>[] {
  const segments: ReactElement<MessageProps>[][] = [];
  let currentRun: ReactElement<MessageProps>[] = [];

  for (const message of messages) {
    if (message.props.variant === 'ghost') {
      if (currentRun.length > 0) {
        segments.push(currentRun);
        currentRun = [];
      }
      segments.push([message]);
    } else {
      currentRun.push(message);
    }
  }
  if (currentRun.length > 0) segments.push(currentRun);

  return segments.flatMap((segment) => {
    const count = segment.length;
    return segment.map((message, i) => {
      let stackPosition: MessageProps['stackPosition'];
      if (count === 1) stackPosition = 'solo';
      else if (i === 0) stackPosition = 'top';
      else if (i === count - 1) stackPosition = 'bottom';
      else stackPosition = 'middle';
      return cloneElement(message, { stackPosition });
    });
  });
}

const styles = stylex.create({
  avatarArea: {
    gridArea: 'avatar',
    alignSelf: 'end',
  },
  base: {
    listStyle: 'none',
    display: 'grid',
    // paddingRight: 16,
  },
  messageList: {
    gridArea: 'messages',
    margin: 0,
    padding: 0,
    gap: 2,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '90%',
  },
  received: {
    gridTemplateAreas: '". sender-name" "avatar messages"',
    columnGap: 8,
    gridTemplateColumns: '32px 1fr',
    rowGap: 2,
  },
  senderNameArea: {
    gridArea: 'sender-name',
  },
  sent: {
    gridTemplateAreas: '"sender-name" "messages"',
    gridTemplateColumns: '1fr',
    justifyItems: 'end',
    rowGap: 2,
  },
});

export { MESSAGE_GROUP_SLOTS, MessageGroup, MessageGroupContext, type MessageGroupProps };
